/**
 * Чистые helper-функции бизнес-логики «Очереди квот»: формирование запросов
 * поставщикам, скачивание Excel «без поставщика», загрузка ответа.
 * Вынесены из ProcurementQueuePage.tsx (рефакторинг 21.05.2026), логика
 * полностью идентична — компонент только оборачивает их в useCallback с
 * нужными зависимостями.
 */
import type { QueryClient } from '@tanstack/react-query'
import { bulkUpdateItemStatus } from '../../shared/api/requestItems.api'
import { recordProcurementDownload } from '../../shared/api/requests.api'
import { generateProcurementRequest } from '../requests/utils/generateProcurementRequest'
import { parseBatchResponse } from './parseBatchResponse'
import { buildUploadSummary } from './uploadSummary'
import {
  bulkCreateProcurementRequests,
  type BulkSupplierInput,
} from '../../shared/api/procurementRequests.api'
import type { QueueRow } from './queueTypes'

/** Закупщик подтвердил выбор поставщиков в SupplierSelectModal: создаём
 *  по ProcurementRequest на каждого + скачиваем XLSX-файл, проставляем
 *  per-user state=REQUESTED и переводим NEW→IN_QUEUE. Отдельно —
 *  прямой запрос производителю (claim): занятые коллегой позиции в его BOM
 *  не входят, по ним возвращается отчёт (текст для toast). */
export async function confirmSuppliers(params: {
  selectedRows: QueueRow[]
  // Поставщики с опциональными персональными наборами позиций (раскладка
  // «Разложить по поставщикам»): набор не задан → все выбранные позиции.
  suppliers: BulkSupplierInput[]
  note: string | null
  manufacturer?: string | null
  queryClient: QueryClient
  invalidateQueue: () => void
  setSupplierModalOpen: (v: boolean) => void
}): Promise<string | null> {
  const {
    selectedRows,
    suppliers,
    note,
    manufacturer,
    queryClient,
    invalidateQueue,
    setSupplierModalOpen,
  } = params
  if (selectedRows.length === 0) return null
  const itemIds = selectedRows.map((r) => r._raw.requestItemId)
  const rowByItemId = new Map(selectedRows.map((r) => [r._raw.requestItemId, r]))

  // 1. Создаём ProcurementRequest на каждого поставщика (+ производителя,
  //    если указан). Бэк генерирует уникальный fileCode для каждого.
  const { requests: created, manufacturerReport } = await bulkCreateProcurementRequests({
    requestItemIds: itemIds,
    suppliers,
    manufacturer: manufacturer ? { name: manufacturer } : undefined,
    note: note ?? undefined,
  })

  // 2. На каждый созданный запрос — генерируем свой XLSX-файл с
  //    предзаполненной колонкой Supplier и fileCode в свойствах документа.
  //    BOM производителю собирается только из вошедших позиций (req.items) —
  //    занятые коллегой туда не попали (частичная сборка).
  for (const req of created) {
    const bomItems = req.items
      .map((bi) => (bi.requestItemId ? rowByItemId.get(bi.requestItemId) : undefined))
      .filter((r): r is QueueRow => !!r)
    await generateProcurementRequest({
      clientCode: null,
      requestNumber: req.fileCode,
      isBatch: true,
      supplierName: req.supplierName,
      fileCode: req.fileCode,
      items: bomItems.map((r) => ({
        requestItemId: r._raw.requestItemId,
        clientCode: r._raw.clientCode,
        requestNumber: r._raw.requestNumber,
        partNumber: r.partNumber,
        brand: r._raw.brand,
        quantity: Number(r._raw.quantity),
        comment: r.noteForProcurement || null,
      })),
    })
  }

  // 3. Старая логика: per-user state=REQUESTED, NEW→IN_QUEUE.
  //    Сохраняем для совместимости с procurementStatus заявки. Трогаем только
  //    позиции, реально вошедшие хоть в один BOM: при раскладке «не
  //    отправленные» и занятые производителем остаются «Новая» (NEW).
  const sentIds = new Set(
    created.flatMap((req) =>
      req.items.map((it) => it.requestItemId).filter((v): v is string => v !== null),
    ),
  )
  const touchedRows = selectedRows.filter((r) => sentIds.has(r._raw.requestItemId))
  const byRequest = new Map<string, string[]>()
  for (const r of touchedRows) {
    const list = byRequest.get(r._raw.requestId) ?? []
    list.push(r._raw.requestItemId)
    byRequest.set(r._raw.requestId, list)
  }
  await Promise.allSettled(
    Array.from(byRequest.entries()).map(([reqId, ids]) => recordProcurementDownload(reqId, ids)),
  )
  const toUpdate = touchedRows.filter(
    (r) => r._raw.itemStatus === 'NEW' || r._raw.itemStatus === 'IN_QUEUE',
  )
  if (toUpdate.length > 0) {
    // Один bulk-запрос вместо залпа из N одиночных PATCH (бэк отфильтрует
    // позиции, уже находящиеся в IN_QUEUE). Раньше Promise.allSettled на
    // 200+ позициях пробивал mutation-rate-limit (инцидент 04.06.2026).
    await bulkUpdateItemStatus(
      toUpdate.map((r) => r._raw.requestItemId),
      'IN_QUEUE',
    )
  }

  invalidateQueue()
  void queryClient.invalidateQueries({ queryKey: ['requests'] })
  void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] })
  setSupplierModalOpen(false)

  // Отчёт частичной сборки BOM производителю — «позиции X, Y не вошли:
  // по ним уже отправлен прямой запрос (закупщик, дата)».
  return buildManufacturerReportText(manufacturerReport)
}

/** Текст отчёта об исключённых из BOM производителю позициях (для toast). */
export function buildManufacturerReportText(
  report: { excluded: { partNumber: string; byName: string; byRole: string; at: string }[] } | null,
): string | null {
  if (!report || report.excluded.length === 0) return null
  const list = report.excluded
    .slice(0, 5)
    .map(
      (e) =>
        `${e.partNumber} (${e.byRole} ${e.byName}, ${e.at ? new Date(e.at).toLocaleString('ru-RU') : '—'})`,
    )
    .join('; ')
  const tail = report.excluded.length > 5 ? ` и ещё ${report.excluded.length - 5}` : ''
  return `В BOM производителю не вошли ${report.excluded.length} позиций — по ним уже отправлен прямой запрос: ${list}${tail}`
}

/** Скачивание Excel «без поставщика» — файл «ноунейм», без записи на бэке
 *  и без смены статусов позиций. */
export async function downloadNoSupplier(params: {
  selectedRows: QueueRow[]
  setSupplierModalOpen: (v: boolean) => void
}): Promise<void> {
  const { selectedRows, setSupplierModalOpen } = params
  if (selectedRows.length === 0) return
  await generateProcurementRequest({
    clientCode: null,
    requestNumber: 'ноунейм',
    isBatch: true,
    fileNameOverride: 'ноунейм',
    items: selectedRows.map((r) => ({
      requestItemId: r._raw.requestItemId,
      clientCode: r._raw.clientCode,
      requestNumber: r._raw.requestNumber,
      partNumber: r.partNumber,
      brand: r._raw.brand,
      quantity: Number(r._raw.quantity),
      comment: r.noteForProcurement || null,
    })),
  })
  setSupplierModalOpen(false)
}

/** Парсинг Excel-ответа поставщика из <input type=file>. Возвращает текст
 *  результата для toast — компонент сам выводит его пользователю. */
export async function uploadResponseAction(params: {
  file: File
  // Передаём «сырые» items из API — parseBatchResponse сам делает сопоставление
  items: unknown[]
  queryClient: QueryClient
  invalidateQueue: () => void
}): Promise<string> {
  const { file, items, queryClient, invalidateQueue } = params
  const result = await parseBatchResponse(file, items as never)
  invalidateQueue()
  // Бэк проставил QUOTED (Квота дана) на каждую позицию — обновляем
  // списки заявок и dashboard, чтобы статусы и счётчики пересчитались.
  void queryClient.invalidateQueries({ queryKey: ['requests'] })
  void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  return buildUploadSummary(result)
}
